import { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useAppSelector, selectGroup } from '../../slices/commonSelectors';
import { getUserById } from 'actions/profileActions';
import { useGetRostersForGroup } from './useRoster';
import { useDistributionLists } from '../useDistributionLists';

// Hook to encapsulate all team activation logic used by the create-incident wizard
// - Fetches distribution lists for the selected group
// - Derives ICS teams
// - Computes activated users from selected teams + current user
// - Fetches missing user data as needed
export function useTeamActivation(selectedTeamIds?: string[]) {
  const dispatch = useDispatch();

  // Redux state
  const currentlySelectedGroup = useAppSelector(selectGroup);
  const currentUser = useAppSelector((state: any) => state.app.user);
  const groupGuid: string = currentlySelectedGroup?.group_guid || '';

  const { data: distributionLists = [] } = useDistributionLists(groupGuid);

  // Roster data for the current group
  const { data: rosterData = [] } = useGetRostersForGroup(groupGuid);

  // Narrow to ICS teams
  const icsTeams = useMemo(
    () => distributionLists.filter((team: any) => team.type === 'ics'),
    [distributionLists]
  );

  // Local cache for missing users fetched on-the-fly
  const [fetchedUsers, setFetchedUsers] = useState<Map<string, any>>(new Map());
  const [fetchingUserGuids, setFetchingUserGuids] = useState<Set<string>>(
    new Set()
  );

  // Fetch user data if a team member isn't present in roster
  const fetchMissingUser = useCallback(
    async (userGuid: string) => {
      if (fetchingUserGuids.has(userGuid) || fetchedUsers.has(userGuid)) {
        return;
      }

      setFetchingUserGuids((prev) => new Set(prev).add(userGuid));

      try {
        const userData: any = await dispatch(getUserById(userGuid) as any);
        if (userData) {
          setFetchedUsers((prev) =>
            new Map(prev).set(userGuid, {
              user_guid: userGuid,
              user_name:
                userData.user_name || userData.profile_settings?.userName,
              email_address: userData.email_address,
              role_assignments: [], // No role assignments for missing users
            })
          );
        }
      } catch (error) {
        console.error(`Failed to fetch user data for ${userGuid}:`, error);
      } finally {
        setFetchingUserGuids((prev) => {
          const next = new Set(prev);
          next.delete(userGuid);
          return next;
        });
      }
    },
    [dispatch, fetchingUserGuids, fetchedUsers]
  );

  // Calculate activated users from selected teams + current user
  const activatedUsers = useMemo(() => {
    const userMap = new Map<string, any>();

    // Always add current user as creator
    if (currentUser?.user_guid) {
      const currentUserRoster = rosterData?.find(
        (r: any) => r.user_guid === currentUser.user_guid
      );
      userMap.set(currentUser.user_guid, {
        user_guid: currentUser.user_guid,
        name: currentUser.user_name,
        email_address: currentUser.email_address,
        role:
          currentUserRoster?.role_assignments?.[0]?.name || 'Incident Creator',
        teams: ['Creator'],
      });
    }

    // Add members from selected teams
    (selectedTeamIds || []).forEach((teamId) => {
      const team = distributionLists.find((t: any) => t.id === teamId);
      if (!team) return;

      if (team.members && Array.isArray(team.members)) {
        team.members.forEach((memberGuid) => {
          const rosterMember = rosterData?.find(
            (r: any) => r.user_guid === memberGuid
          );
          const fetchedMember = fetchedUsers.get(memberGuid);

          const isICSTeam = team.type === 'ics';
          const roleForMember = isICSTeam
            ? team.name
            : rosterMember?.role_assignments?.[0]?.name || 'Team Member';

          if (rosterMember) {
            const existingUser = userMap.get(memberGuid);
            const userEntry = {
              user_guid: memberGuid,
              name: rosterMember.user_name,
              email_address: rosterMember.email_address,
              role: roleForMember,
              teams: existingUser
                ? [...existingUser.teams, team.name]
                : [team.name],
            };
            userMap.set(memberGuid, userEntry);
          } else if (fetchedMember) {
            const existingUser = userMap.get(memberGuid);
            const userEntry = {
              user_guid: memberGuid,
              name: fetchedMember.user_name,
              email_address: fetchedMember.email_address,
              role: roleForMember,
              teams: existingUser
                ? [...existingUser.teams, team.name]
                : [team.name],
            };
            userMap.set(memberGuid, userEntry);
          } else {
            // User not found in roster or fetched data - try to fetch
            console.warn(
              `⚠️ Team member ${memberGuid} from team "${team.name}" not found in roster data. Attempting to fetch user data...`
            );
            fetchMissingUser(memberGuid);

            // Include with minimal data for now (will be updated when fetch completes)
            const existingUser = userMap.get(memberGuid);
            const userEntry = {
              user_guid: memberGuid,
              name: 'Loading...',
              email_address: 'Loading...',
              role: roleForMember,
              teams: existingUser
                ? [...existingUser.teams, team.name]
                : [team.name],
            };
            userMap.set(memberGuid, userEntry);
          }
        });
      }
    });

    return Array.from(userMap.values());
  }, [
    selectedTeamIds,
    distributionLists,
    rosterData,
    currentUser,
    fetchedUsers,
    fetchMissingUser,
  ]);

  return {
    icsTeams,
    activatedUsers,
    isFetching: fetchingUserGuids.size > 0,
  } as const;
}
